import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, addDoc, collection } from 'firebase/firestore';
import { ref, deleteObject, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from './firebase';
import MessageBox from './MessageBox';
import ChatBox from './ChatBox';
import NotificationBell from './NotificationBell'; // ✅ Added import

const ItemDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [username, setUsername] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  const [claimDescription, setClaimDescription] = useState('');
  const [isClaimPending, setIsClaimPending] = useState(false);
  const [claimImage, setClaimImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [showLocationModal, setShowLocationModal] = useState(false);
  const [newLocation, setNewLocation] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  const showMessageBox = useCallback((msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
  }, []);

  const closeMessageBox = useCallback(() => {
    setMessage('');
    setMessageType('info');
  }, []);

  const checkAdminStatus = useCallback(async (user) => {
    if (!user) return false;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.role === 'admin';
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
    return false;
  }, []);

  const fetchItem = useCallback(async () => {
    setLoading(true);
    try {
      const docRef = doc(db, 'items', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const itemData = { id: docSnap.id, ...docSnap.data() };
        setItem(itemData);
        
        // ✅ FIXED: Better claim pending check
        if (itemData.claimRequest && 
            itemData.claimRequest.status === 'pending' && 
            itemData.claimRequest.userId) {
          setIsClaimPending(true);
        } else {
          setIsClaimPending(false);
        }
      } else {
        showMessageBox('Item not found.', 'danger');
        setItem(null);
      }
    } catch (error) {
      console.error('Error fetching item:', error);
      showMessageBox('Failed to load item details.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [id, showMessageBox]);

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      if (user) {
        const storedUsername = localStorage.getItem("username");
        setUsername(storedUsername || user.email);
        const adminStatus = await checkAdminStatus(user);
        setIsAdmin(adminStatus);
      } else {
        localStorage.removeItem("username");
        setIsAdmin(false);
        navigate("/login");
      }
    });
    if (id) fetchItem();
    return () => unsubscribeAuth();
  }, [id, navigate, fetchItem, checkAdminStatus]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showMessageBox('Image size must be less than 5MB.', 'warning');
        return;
      }
      setClaimImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const uploadClaimImage = async (file) => {
    const timestamp = Date.now();
    const imageRef = ref(storage, `claim_images/${timestamp}-${file.name}`);
    const snapshot = await uploadBytes(imageRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  // ✅ UPDATED handleApproveClaim with New Notification Structure
  const handleApproveClaim = async () => {
    if (!item || !item.claimRequest || !item.claimRequest.userId) return;
    
    try {
      const itemRef = doc(db, "items", item.id);
      await updateDoc(itemRef, {
        claimedBy: item.claimRequest.userId,
        status: 'claimed',
        claimedAt: new Date().toISOString(),
        claimRequest: {} // Clear the claim request
      });

      // ✅ CREATE APPROVAL NOTIFICATION
      await addDoc(collection(db, 'notifications'), {
        userId: item.claimRequest.userId,
        itemId: item.id,
        itemName: item.itemName || item.foundItemName || 'Unknown Item',
        type: 'claim_approved',
        title: 'Claim Approved! 🎉',
        message: `Your claim for "${item.itemName || item.foundItemName || 'Unknown Item'}" has been approved! Please contact the finder to arrange collection.`,
        read: false,
        createdAt: new Date().toISOString(),
        data: {
          itemLocation: item.currentLocation || 'Unknown',
          approvedBy: isAdmin ? 'Admin' : 'Item Owner',
          contactInfo: item.username || 'Item finder'
        },
        priority: 'high'
      });

      // Log admin action if admin approved
      if (isAdmin) {
        await addDoc(collection(db, 'admin_logs'), {
          action: 'approve_claim',
          itemId: item.id,
          adminId: currentUser.uid,
          timestamp: new Date().toISOString(),
          reason: 'Claim approved by admin'
        });
      }

      showMessageBox('Claim approved! Claimer has been notified.', 'success');
      setIsClaimPending(false);
      fetchItem();
    } catch (error) {
      console.error('Error approving claim:', error);
      showMessageBox('Failed to approve claim: ' + error.message, 'danger');
    }
  };

  // ✅ UPDATED handleRejectClaim with New Notification Structure
  const handleRejectClaim = async () => {
    if (!item || !item.claimRequest) return;
    
    const confirmReject = window.confirm("Are you sure you want to reject this claim? The claimer will be notified.");
    if (!confirmReject) return;

    try {
      const claimerUserId = item.claimRequest.userId;
      const claimerUsername = item.claimRequest.username;
      
      const itemRef = doc(db, 'items', item.id);
      
      // Clear the claim request completely
      await updateDoc(itemRef, {
        claimRequest: {}, 
        status: item.type === 'found' ? 'unclaimed' : item.status
      });

      // ✅ CREATE REJECTION NOTIFICATION
      await addDoc(collection(db, 'notifications'), {
        userId: claimerUserId,
        itemId: item.id,
        itemName: item.itemName || item.foundItemName || 'Unknown Item',
        type: 'claim_rejected',
        title: 'Claim Not Approved',
        message: `Your claim for "${item.itemName || item.foundItemName || 'Unknown Item'}" has been rejected. The item remains available for other claims.`,
        read: false,
        createdAt: new Date().toISOString(),
        data: {
          rejectedBy: isAdmin ? 'Admin' : 'Item Owner',
          reason: 'Insufficient proof of ownership',
          itemStillAvailable: true
        },
        priority: 'normal'
      });

      // Log admin action if admin rejected
      if (isAdmin) {
        await addDoc(collection(db, 'admin_logs'), {
          action: 'reject_claim',
          itemId: item.id,
          adminId: currentUser.uid,
          timestamp: new Date().toISOString(),
          reason: 'Claim rejected by admin',
          rejectedClaimerUsername: claimerUsername
        });
      }

      showMessageBox('Claim rejected. The claimer has been notified.', 'info');
      setIsClaimPending(false);
      fetchItem();
    } catch (error) {
      console.error('Error rejecting claim:', error);
      showMessageBox('Failed to reject claim: ' + error.message, 'danger');
    }
  };

  const handleClaimItem = async () => {
    setShowClaimModal(false);
    if (!item || item.type !== 'found' || item.claimedBy) {
      showMessageBox('This item cannot be claimed or is already claimed.', 'danger');
      return;
    }
    if (!currentUser) {
      showMessageBox('You must be logged in to claim an item.', 'danger');
      navigate('/login');
      return;
    }
    if (!claimDescription.trim()) {
      showMessageBox('Please enter a description/proof for your claim.', 'warning');
      return;
    }

    setUploading(true);
    try {
      const itemRef = doc(db, "items", item.id);
      const claimRequestData = {
        userId: currentUser.uid,
        username: username,
        description: claimDescription,
        status: 'pending',
        requestedAt: new Date().toISOString(),
      };

      if (claimImage) {
        const imageUrl = await uploadClaimImage(claimImage);
        claimRequestData.photoURL = imageUrl;
      }

      await updateDoc(itemRef, {
        claimRequest: claimRequestData
      });

      showMessageBox('Claim request submitted for review!', 'success');
      setIsClaimPending(true);
      setClaimDescription('');
      setClaimImage(null);
      setImagePreview(null);
      fetchItem();
    } catch (error) {
      showMessageBox('Failed to submit claim: ' + error.message, 'danger');
    } finally {
      setUploading(false);
    }
  };

  const handleAdminClaimOfficeItem = async () => {
    if (!isAdmin || !item) return;
    if (window.confirm("Mark this item as collected from the Lost and Found Office?")) {
      try {
        const itemRef = doc(db, "items", item.id);
        await updateDoc(itemRef, {
          status: 'collected',
          collectedAt: new Date().toISOString(),
          collectedBy: 'admin',
          adminAction: {
            type: 'office_collection',
            adminId: currentUser.uid,
            timestamp: new Date().toISOString()
          }
        });

        // Log admin action
        await addDoc(collection(db, 'admin_logs'), {
          action: 'collect_office_item',
          itemId: item.id,
          adminId: currentUser.uid,
          timestamp: new Date().toISOString(),
          reason: 'Item collected from Lost and Found Office'
        });

        showMessageBox('Item marked as collected from office!', 'success');
        fetchItem();
      } catch (error) {
        showMessageBox('Failed to mark item as collected: ' + error.message, 'danger');
      }
    }
  };

  const canEditLocation = (item &&
    currentUser &&
    (currentUser.uid === item.userId || isAdmin) &&
    item.type === 'found' &&
    (item.currentLocation !== 'Lost and Found Office' || isAdmin));

  const handleLocationUpdate = () => {
    if (!newLocation.trim()) {
      showMessageBox('Enter a valid location.', 'warning');
      return;
    }
    const itemRef = doc(db, 'items', item.id);
    updateDoc(itemRef, { currentLocation: newLocation })
      .then(async () => {
        // Log admin action if admin updated location
        if (isAdmin) {
          await addDoc(collection(db, 'admin_logs'), {
            action: 'update_location',
            itemId: item.id,
            adminId: currentUser.uid,
            timestamp: new Date().toISOString(),
            reason: `Location updated to: ${newLocation}`
          });
        }

        showMessageBox('Location updated successfully!', 'success');
        setShowLocationModal(false);
        setNewLocation('');
        fetchItem();
      })
      .catch(error => {
        showMessageBox('Failed to update location: ' + error.message, 'danger');
      });
  };

  const handleDeleteItem = async () => {
    setShowDeleteModal(false);
    if (!item) return;

    // Check permissions: owner OR admin
    const isOwner = currentUser && item.userId === currentUser.uid;
    if (!currentUser || (!isOwner && !isAdmin)) {
      showMessageBox('You do not have permission to delete this item.', 'danger');
      return;
    }

    const confirmMessage = isAdmin 
      ? "Are you sure you want to delete this item as an admin? This action cannot be undone and will be logged."
      : "Are you sure you want to delete this item? This action cannot be undone.";

    if (window.confirm(confirmMessage)) {
      try {
        // Log admin action before deletion
        if (isAdmin) {
          await addDoc(collection(db, 'admin_logs'), {
            action: 'delete_item',
            itemId: item.id,
            itemName: item.itemName || item.lostItemName || item.foundItemName,
            adminId: currentUser.uid,
            timestamp: new Date().toISOString(),
            reason: 'Item deleted by admin',
            originalOwner: item.userId,
            itemLocation: item.currentLocation
          });
        }

        // Delete image from storage if it exists
        if (item.imageUrl) {
          const imageRef = ref(storage, item.imageUrl);
          try {
            await deleteObject(imageRef);
          } catch (storageError) {
            if (storageError.code !== 'storage/object-not-found') {
              console.warn('Failed to delete image from storage:', storageError);
            }
          }
        }

        // Delete the item document
        const itemRef = doc(db, 'items', item.id);
        await deleteDoc(itemRef);
        
        const successMessage = isAdmin 
          ? 'Item successfully deleted by admin!' 
          : 'Item successfully deleted!';
        
        showMessageBox(successMessage, 'success');
        setTimeout(() => navigate('/homepage'), 1500);
      } catch (error) {
        console.error('Error deleting item:', error);
        showMessageBox('Failed to delete item: ' + error.message, 'danger');
      }
    }
  };

  if (loading || currentUser === null) {
    return (
      <div className="min-h-screen flex flex-col bg-white animate-pulse select-none">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="max-w-xl mx-auto my-20 text-center">
        {message && <MessageBox message={message} type={messageType} onClose={closeMessageBox} />}
        <h3 className="text-red-600 font-bold text-2xl">Item not found or an error occurred.</h3>
        <Link to="/homepage" className="mt-6 inline-block bg-blue-600 text-white px-5 py-3 rounded-full font-bold shadow hover:bg-blue-700 transition">Go to Homepage</Link>
      </div>
    );
  }

  const isOwner = currentUser && item.userId === currentUser.uid;
  
  // ✅ FIXED: Better isClaimable logic
  const isClaimable =
    item.type === 'found' &&
    !item.claimedBy &&
    (!item.claimRequest || !item.claimRequest.userId || !item.claimRequest.status) &&
    (item.currentLocation !== "Lost and Found Office" || isAdmin);

  // ✅ FIXED: Better check for showing claim request section
  const hasActiveClaim = item.claimRequest && 
                        item.claimRequest.userId && 
                        item.claimRequest.status === 'pending';

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    navigate("/login");
  };

  return (
    <>
      {/* Navbar */}
      <nav className="flex items-center bg-white border-b px-6 py-3 shadow sticky top-0 z-40">
        <Link className="mr-2" to="/homepage">
          <img src="/Topbar-christ.jpg" alt="Christ University Logo" className="logo-christ max-w-[150px] h-auto" />
        </Link>
        <div className="ml-auto flex items-center gap-3">
          {/* ✅ Added NotificationBell component here */}
          <NotificationBell />
          <span className="font-bold text-blue-900">
            Welcome: {username}
            {isAdmin && <span className="ml-2 px-2 py-1 rounded-lg bg-yellow-200 text-yellow-900 text-xs font-semibold">Admin</span>}
          </span>
          <ul className="flex gap-2 items-center ml-6 text-sm font-semibold">
            <li>
              <Link className="my-nav-link px-2 py-1 text-blue-900 hover:underline" to="/homepage">HOME</Link>
            </li>
            <li>
              <Link className="my-nav-link px-2 py-1 text-blue-900 hover:underline" to="/history">HISTORY</Link>
            </li>
            <li>
              <a className="my-nav-link px-2 py-1 text-blue-900 hover:underline" href="https://www.christuniversity.in/" target="_blank" rel="noopener noreferrer">ABOUT</a>
            </li>
            {isAdmin && (
              <li>
                <Link className="my-nav-link px-2 py-1 text-yellow-700 hover:underline" to="/admin/dashboard">ADMIN PANEL</Link>
              </li>
            )}
            <li>
              <button
                onClick={handleLogout}
                type="button"
                className="bg-white text-center w-48 rounded-2xl h-14 relative text-black text-xl font-semibold group"
              >
                <div className="bg-red-400 rounded-xl h-12 w-1/4 flex items-center justify-center absolute left-1 top-[4px] group-hover:w-[184px] z-10 duration-500">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" height="25px" width="25px">
                    <path d="M224 480h640a32 32 0 1 1 0 64H224a32 32 0 0 1 0-64z" fill="#000000"></path>
                    <path d="m237.248 512 265.408 265.344a32 32 0 0 1-45.312 45.312l-288-288a32 32 0 0 1 0-45.312l288-288a32 32 0 1 1 45.312 45.312L237.248 512z" fill="#000000"></path>
                  </svg>
                </div>
                <p className="translate-x-2">LOGOUT</p>
              </button>
            </li>
          </ul>
        </div>
      </nav>
      <hr className="h-1 border-0 bg-gradient-to-r from-blue-100 via-green-100 to-blue-100 mb-0" />

      <div className="max-w-4xl mx-auto my-8 px-6">
        {message && <MessageBox message={message} type={messageType} onClose={closeMessageBox} />}

        <div className="bg-white rounded-3xl shadow-xl p-7">
          <h2 className={`mb-6 text-center font-bold text-2xl ${item.type === 'lost' ? 'text-red-600' : 'text-green-700'}`}>
            {item.type === 'lost' ? 'Lost Item Details' : 'Found Item Details'}
          </h2>

          <div className="flex flex-col md:flex-row gap-10">
            <div className="md:w-1/2 flex flex-col items-center justify-center mb-6 md:mb-0">
              {item.imageUrl ? (
                <img src={item.imageUrl} alt={item.itemName || item.lostItemName || item.foundItemName} className="w-full max-h-80 object-cover rounded-xl shadow" />
              ) : (
                <div className="flex items-center justify-center w-full h-64 bg-gray-100 text-gray-400 rounded-xl">
                  No Image Available
                </div>
              )}
            </div>
            <div className="md:w-1/2">
              <h3 className="text-xl font-bold mb-2">{item.itemName || item.lostItemName || item.foundItemName}</h3>
              <p className="text-gray-500 mb-3">{item.description}</p>
              <ul className="text-sm mb-5 text-gray-700 space-y-1">
                <li><span className="font-semibold">Category:</span> {item.category}</li>
                <li><span className="font-semibold">Location:</span> {item.location}, {item.building}, {item.classroom}</li>
                <li>
                  <span className="font-semibold">Date {item.type === 'lost' ? 'Lost' : 'Found'}:</span> {item.reportedDate || item.dateReported}
                </li>
                {(item.timeReported || item.reportedTime) && (
                  <li>
                    <span className="font-semibold">Time {item.type === 'lost' ? 'Lost' : 'Found'}:</span> {item.timeReported || item.reportedTime}
                  </li>
                )}
                <li><span className="font-semibold">Reported By:</span> {item.username}</li>
                {item.type === 'found' && item.foundNotes && (
                  <li><span className="font-semibold">Found Notes:</span> {item.foundNotes}</li>
                )}
                <li>
                  <span className="font-semibold">Status:</span>{' '}
                  <span className={`ml-1 px-2 py-1 rounded-full text-xs font-semibold
                      ${item.status === 'lost' ? 'bg-red-100 text-red-700' :
                        item.status === 'found' ? 'bg-green-100 text-green-700' :
                          item.status === 'claimed' ? 'bg-blue-100 text-blue-700' :
                            item.status === 'collected' ? 'bg-gray-200 text-gray-700' : 'bg-gray-200 text-gray-700'}`}>
                    {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                  </span>
                </li>
                <li>
                  <span className="font-semibold">Current Location: </span>{item.currentLocation || "Unknown"}
                  {canEditLocation && (
                    <button
                      className="ml-3 bg-blue-50 border border-blue-300 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full hover:bg-blue-100 transition"
                      onClick={() => setShowLocationModal(true)}
                    >
                      {isAdmin ? 'Admin Edit' : 'Edit'}
                    </button>
                  )}
                </li>
                {item.claimedBy && <li><span className="font-semibold">Claimed By:</span> {item.claimedBy}</li>}
                {item.status === 'collected' && (
                  <li>
                    <span className="font-semibold">Collected At:</span> {item.collectedAt ? new Date(item.collectedAt).toLocaleDateString() : 'Unknown'}
                  </li>
                )}
              </ul>

              {isAdmin && item.currentLocation === "Lost and Found Office" && (
                <div className="my-3 px-4 py-3 rounded-lg bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 font-semibold">
                  <i className="bi bi-gear me-2"></i>
                  Admin View: This item is at the Lost and Found Office. You have administrative control.
                </div>
              )}

              {/* ✅ FIXED: Only show claim request section when there's an active claim */}
              {hasActiveClaim && (isOwner || isAdmin) && (
                <div className="my-3 px-4 py-3 rounded-lg bg-blue-50 border-l-4 border-blue-300">
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-blue-800">Claim Request from {item.claimRequest.username}</span>
                      <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-semibold">
                        Pending Review
                      </span>
                    </div>
                    <div className="text-gray-700 mb-3">
                      <strong>Proof/Description:</strong>
                      <p className="mt-1 bg-white p-2 rounded border">{item.claimRequest.description}</p>
                    </div>
                    {item.claimRequest.photoURL && (
                      <div className="mb-3">
                        <strong className="text-gray-700">Submitted Proof Image:</strong>
                        <div className="mt-2">
                          <img
                            src={item.claimRequest.photoURL}
                            alt="Claim Proof"
                            className="rounded border max-w-[300px] max-h-[200px] object-cover shadow-md"
                          />
                        </div>
                      </div>
                    )}
                    <div className="text-xs text-gray-500">
                      <strong>Submitted:</strong> {new Date(item.claimRequest.requestedAt).toLocaleString()}
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      className="py-2 px-4 rounded-full bg-green-600 text-white font-bold hover:bg-green-700 transition"
                      onClick={handleApproveClaim}
                    >
                      <i className="bi bi-check-circle me-1"></i>
                      {isAdmin ? 'Admin Approve' : 'Approve Claim'}
                    </button>
                    <button
                      className="py-2 px-4 rounded-full bg-red-600 text-white font-bold hover:bg-red-700 transition"
                      onClick={handleRejectClaim}
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      {isAdmin ? 'Admin Reject' : 'Reject Claim'}
                    </button>
                  </div>
                </div>
              )}

              {item.type === 'found' && item.currentLocation === "Lost and Found Office" && !isAdmin ? (
                <div className="my-3 px-4 py-3 rounded-lg bg-green-50 border-l-4 border-green-400 text-green-800 font-semibold">
                  This item is at the Lost and Found Office. Please go directly to the office with your ID to claim it. No chat needed.
                </div>
              ) : (
                <>
                  {isClaimPending && !isAdmin && (
                    <div className="my-3 px-4 py-3 rounded-lg bg-blue-50 border-l-4 border-blue-300 text-blue-700 font-semibold">
                      Your claim is pending review.
                    </div>
                  )}

                  {item.currentLocation !== "Lost and Found Office" && (
                    <ChatBox itemId={item.id} currentUser={currentUser} />
                  )}

                  <div className="flex flex-wrap gap-3 mt-6">
                    {isClaimable && (
                      <button
                        className="py-3 px-7 bg-green-600 text-white text-base rounded-full font-bold hover:bg-green-700 transition"
                        onClick={() => setShowClaimModal(true)}
                      >
                        {isAdmin && item.currentLocation === "Lost and Found Office" ? 'Admin Claim' : 'Claim Item'}
                      </button>
                    )}

                    {isAdmin && item.currentLocation === "Lost and Found Office" && !item.claimedBy && item.status !== 'collected' && (
                      <button
                        className="py-3 px-7 bg-yellow-400 text-gray-900 text-base rounded-full font-bold border border-yellow-500 hover:bg-yellow-500 transition"
                        onClick={handleAdminClaimOfficeItem}
                      >
                        <i className="bi bi-check-circle me-1"></i>
                        Mark as Collected (Admin)
                      </button>
                    )}

                    {(isOwner || isAdmin) && (
                      <button
                        className="py-3 px-7 bg-red-600 text-white text-base rounded-full font-bold hover:bg-red-700 transition"
                        onClick={() => setShowDeleteModal(true)}
                      >
                        <i className="bi bi-trash me-1"></i>
                        {isAdmin ? 'Admin Delete' : 'Delete Item'}
                      </button>
                    )}

                    <div className="ml-auto flex items-center">
                      <Link
                        to="/homepage"
                        className="bg-gray-600 text-white font-medium border border-gray-700 border-b-4 px-4 py-2 rounded-full hover:brightness-110 hover:border-t-4 hover:border-b active:opacity-80 transition duration-300"
                      >
                        <i className="bi bi-arrow-left me-2"></i>
                        Back to Home
                      </Link>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Claim Modal with Image Upload */}
      {showClaimModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <h4 className="font-bold text-lg mb-3">
              {isAdmin && item.currentLocation === "Lost and Found Office" ? 'Admin Claim Item' : 'Claim Item'}
            </h4>
            <p className="mb-2 text-gray-500 text-sm">
              {isAdmin && item.currentLocation === "Lost and Found Office"
                ? 'As an admin, you can claim this item on behalf of a user or mark it for collection.'
                : 'Please describe your item in detail and optionally provide a photo as proof of ownership.'
              }
            </p>
            <textarea
              className="w-full border px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 mb-3"
              value={claimDescription}
              onChange={e => setClaimDescription(e.target.value)}
              rows={3}
              placeholder={isAdmin ? "Admin claim reason or user details..." : "e.g. Blue backpack, Iron Man keychain, sticker inside flap..."}
            />

            {/* Image Upload Section */}
            <div className="mb-3">
              <label className="block mb-1 font-medium text-gray-600">Upload Proof Image (optional)</label>
              <input
                type="file"
                className="w-full border rounded-lg p-2 bg-gray-50"
                accept="image/*"
                onChange={handleImageChange}
              />
              <small className="text-gray-400 text-xs">Maximum file size: 5MB</small>
            </div>

            {imagePreview && (
              <div className="my-2 flex justify-center">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-w-full max-h-48 object-cover rounded shadow"
                />
              </div>
            )}

            <div className="flex gap-3 mt-4">
              <button
                className="flex-1 py-2 px-4 bg-green-600 text-white rounded-full font-bold hover:bg-green-700 transition disabled:opacity-50"
                onClick={handleClaimItem}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></span>
                    Submitting...
                  </>
                ) : (
                  isAdmin ? 'Submit Admin Claim' : 'Submit Claim'
                )}
              </button>
              <button
                className="flex-1 py-2 px-4 bg-gray-300 text-gray-800 rounded-full font-bold hover:bg-gray-400 transition disabled:opacity-50"
                onClick={() => {
                  setShowClaimModal(false);
                  setClaimImage(null);
                  setImagePreview(null);
                  setClaimDescription('');
                }}
                disabled={uploading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Location Update Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <h4 className="font-bold text-lg mb-3">
              {isAdmin ? 'Admin: Update Location' : 'Update Location'}
            </h4>
            <p className="mb-2 text-gray-500 text-sm">
              Set the current location of the item.
              {!isAdmin && (
                <>
                  <br />
                  <span className="text-red-600 font-bold">Once set to "Lost and Found Office", you can't change it again!</span>
                </>
              )}
              {isAdmin && (
                <>
                  <br />
                  <span className="text-blue-700 font-bold">As an admin, you can update locations without restrictions.</span>
                </>
              )}
            </p>
            <input
              className="w-full border px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 mb-3"
              value={newLocation}
              onChange={e => setNewLocation(e.target.value)}
              placeholder='e.g. "With Founder", "Lost and Found Office", etc.'
            />
            <div className="flex gap-3 mt-4">
              <button
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-full font-bold hover:bg-blue-700 transition"
                onClick={handleLocationUpdate}
              >
                {isAdmin ? 'Admin Update' : 'Update Location'}
              </button>
              <button
                className="flex-1 py-2 px-4 bg-gray-300 text-gray-800 rounded-full font-bold hover:bg-gray-400 transition"
                onClick={() => setShowLocationModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center mb-4">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <i className="bi bi-exclamation-triangle text-red-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-lg text-gray-900">
                {isAdmin ? 'Admin: Confirm Deletion' : 'Confirm Deletion'}
              </h4>
            </div>
            <p className="mb-4 text-gray-500 text-sm text-center">
              Are you sure you want to delete this item? This action cannot be undone.
              {isAdmin && (
                <>
                  <br />
                  <span className="text-red-700 font-bold">This action will be logged as an admin deletion.</span>
                </>
              )}
            </p>
            <div className="flex gap-3 mt-6">
              <button
                className="flex-1 py-2 px-4 bg-red-600 text-white rounded-full font-bold hover:bg-red-700 transition"
                onClick={handleDeleteItem}
              >
                {isAdmin ? 'Admin Delete' : 'Yes, Delete'}
              </button>
              <button
                className="flex-1 py-2 px-4 bg-gray-300 text-gray-800 rounded-full font-bold hover:bg-gray-400 transition"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="w-full bg-blue-900 text-gray-100 pt-10 pb-5 shadow-inner border-t-2 border-blue-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 pb-8 text-left">
            {/* Quick Links */}
            <div>
              <h6 className="font-bold mb-4 text-lg text-white">Quick Links</h6>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/homepage" className="text-gray-300 hover:text-white underline underline-offset-2 transition-all duration-200">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/reportitem" className="text-gray-300 hover:text-white underline underline-offset-2 transition-all duration-200">
                    Report Item
                  </Link>
                </li>
                <li>
                  <Link to="/history" className="text-gray-300 hover:text-white underline underline-offset-2 transition-all duration-200">
                    Claims History
                  </Link>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h6 className="font-bold mb-4 text-lg text-white">Services</h6>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/all-lost-items" className="text-gray-300 hover:text-white underline underline-offset-2 transition-all duration-200">
                    Lost Items
                  </Link>
                </li>
                <li>
                  <Link to="/all-found-items" className="text-gray-300 hover:text-white underline underline-offset-2 transition-all duration-200">
                    Found Items
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h6 className="font-bold mb-4 text-lg text-white">Contact</h6>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <span className="inline-block w-5 mr-2">📍</span>
                  Christ University, Bangalore
                </li>
                <li>
                  <span className="inline-block w-5 mr-2">✉️</span>
                  info@christuniversity.in
                </li>
                <li>
                  <span className="inline-block w-5 mr-2">📞</span>
                  +91 12345 67890
                </li>
              </ul>
            </div>
          </div>

          <hr className="border-gray-600 border-opacity-30 my-6" />

          <div className="text-center">
            <small className="text-gray-400 text-xs tracking-wide">
              © 2024 Lost and Found Christ University. All rights reserved.
            </small>
          </div>
        </div>
      </footer>
    </>
  );
};

export default ItemDetailsPage;
